import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './user-viewer.style.js';
import { updateUser } from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import { UserRole, UserPermissions } from '@cmms/models';

interface User {
  id: string;
  model: {
    user_id: string;
    company_id: string;
    name: string;
    email: string;
    role: UserRole;
    permissions?: UserPermissions;
    created_at: number;
    updated_at: number;
  };
  authData?: {
    lastSignInTime: string | null;
    disabled: boolean;
    emailVerified: boolean;
  };
}

@customElement('user-viewer')
export class UserViewer extends LitElement {
  @property({ type: Boolean })
  open = false;

  @property({ type: Object })
  user: User | null = null;

  @property({ type: Object })
  firestore: Firestore = null as any;

  @state()
  private isEditing = false;

  @state()
  private isLoading = false;

  @state()
  private errorMessage = '';

  @state()
  private originalUser: User | null = null;

  static styles = styles;

  private formatDate(timestamp: number): string {
    if (!timestamp) return 'Unknown Date';
    return new Date(timestamp).toLocaleDateString();
  }

  private getRoleDisplayName(role: UserRole): string {
    switch (role) {
      case UserRole.SUPER_ADMIN:
        return 'Super Administrator';
      case UserRole.ADMIN:
        return 'Administrator';
      case UserRole.ENGINEER:
        return 'Engineer';
      case UserRole.SHOP_FLOOR:
        return 'Shop Floor';
      default:
        return role;
    }
  }

  private getRoleOptions() {
    return [
      { value: UserRole.SUPER_ADMIN, label: 'Super Administrator' },
      { value: UserRole.ADMIN, label: 'Administrator' },
      { value: UserRole.ENGINEER, label: 'Engineer' },
      { value: UserRole.SHOP_FLOOR, label: 'Shop Floor' }
    ];
  }

  private async handleSave() {
    if (!this.user) return;

    try {
      this.isLoading = true;
      this.errorMessage = '';

      const updateData: any = {
        name: this.user.model.name,
        email: this.user.model.email,
        role: this.user.model.role,
        permissions: this.user.model.permissions || {},
        company_id: this.user.model.company_id,
        updated_at: Date.now()
      };

      const result = await updateUser(this.firestore, this.user.id, updateData);
      
      if (!result.error) {
        this.user = { ...this.user, model: { ...this.user.model, ...updateData } };
        this.isEditing = false;
        this.originalUser = null;
        
        // Dispatch event to notify parent component
        this.dispatchEvent(new CustomEvent('user-updated', {
          detail: result.data,
          bubbles: true,
          composed: true
        }));
      } else {
        this.errorMessage = result.error || 'Failed to update user';
      }
    } catch (error: any) {
      console.error('Error updating user:', error);
      this.errorMessage = error.message || 'Failed to update user';
    } finally {
      this.isLoading = false;
    }
  }

  private handleEdit() {
    this.originalUser = { ...this.user! };
    this.isEditing = true;
    this.errorMessage = '';
  }

  private handleCancel() {
    if (this.originalUser) {
      this.user = { ...this.originalUser };
    }
    this.isEditing = false;
    this.originalUser = null;
    this.errorMessage = '';
  }

  private handleFieldUpdate(field: string, value: any) {
    if (this.user) {
      this.user = {
        ...this.user,
        model: {
          ...this.user.model,
          [field]: value
        }
      };
    }
  }

  private handlePermissionChange(resource: keyof UserPermissions, action: 'read' | 'write' | 'delete', checked: boolean) {
    if (!this.user) return;

    const permissions = { ...(this.user.model.permissions || {}) };
    const resourcePermissions = [...(permissions[resource] || [])];

    if (checked) {
      if (!resourcePermissions.includes(action)) {
        resourcePermissions.push(action);
      }
    } else {
      const index = resourcePermissions.indexOf(action);
      if (index > -1) {
        resourcePermissions.splice(index, 1);
      }
    }

    permissions[resource] = resourcePermissions.length > 0 ? resourcePermissions : undefined;

    this.user = {
      ...this.user,
      model: {
        ...this.user.model,
        permissions
      }
    };
  }

  private hasPermission(resource: keyof UserPermissions, action: 'read' | 'write' | 'delete'): boolean {
    if (!this.user?.model.permissions) return false;
    return this.user.model.permissions[resource]?.includes(action) || false;
  }

  private getPermissionResources() {
    return [
      { key: 'assets', label: 'Assets' },
      { key: 'work_orders', label: 'Work Orders' },
      { key: 'locations', label: 'Locations' },
      { key: 'preventive_maintenance', label: 'Preventive Maintenance' },
      { key: 'parts', label: 'Parts' },
      { key: 'parts_usage', label: 'Parts Usage' },
      { key: 'downtimes', label: 'Downtimes' },
      { key: 'safety_reports', label: 'Safety Reports' },
      { key: 'safety_actions', label: 'Safety Actions' },
      { key: 'users', label: 'Users' },
      { key: 'companies', label: 'Companies' }
    ];
  }

  private closeModal() {
    this.dispatchEvent(new CustomEvent('modal-closed', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (!this.user) {
      return html``;
    }

    const userName = this.user.model.name;
    const userRole = this.getRoleDisplayName(this.user.model.role);

    return html`
      <sl-dialog
        label="User Viewer"
        .open=${this.open}
        @sl-hide=${this.closeModal}
      >
        <div class="user-header">
          <h2>${userName}</h2>
          <div class="user-role-badge">
            <sl-badge variant="primary">${userRole}</sl-badge>
          </div>
        </div>

        ${this.errorMessage ? html`
          <div class="error-message">
            <sl-icon name="exclamation-triangle" style="margin-right: 0.5rem;"></sl-icon>
            ${this.errorMessage}
          </div>
        ` : ''}

        <div class="user-fields">
          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Name:</div>
            <div class="field-value">
              <sl-input
                .value=${this.user.model.name}
                @sl-input=${(e: Event) => this.handleFieldUpdate('name', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                ?disabled=${!this.isEditing}
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Email:</div>
            <div class="field-value">
              <sl-input
                .value=${this.user.model.email}
                @sl-input=${(e: Event) => this.handleFieldUpdate('email', (e.target as HTMLInputElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                type="email"
                size="large"
                ?disabled=${!this.isEditing}
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Role:</div>
            <div class="field-value">
              <sl-select
                .value=${this.user.model.role}
                @sl-change=${(e: Event) => this.handleFieldUpdate('role', (e.target as HTMLSelectElement).value)}
                @click=${(e: Event) => e.stopPropagation()}
                @sl-hide=${(e: Event) => e.stopPropagation()}
                @sl-show=${(e: Event) => e.stopPropagation()}
                @sl-after-show=${(e: Event) => e.stopPropagation()}
                @sl-after-hide=${(e: Event) => e.stopPropagation()}
                ?disabled=${!this.isEditing}
              >
                ${this.getRoleOptions().map(option => html`
                  <sl-option value=${option.value}>${option.label}</sl-option>
                `)}
              </sl-select>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Created:</div>
            <div class="field-value">
              <sl-input
                .value=${this.formatDate(this.user.model.created_at)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                disabled
              ></sl-input>
            </div>
          </div>

          <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
            <div class="field-label">Last Updated:</div>
            <div class="field-value">
              <sl-input
                .value=${this.formatDate(this.user.model.updated_at)}
                @click=${(e: Event) => e.stopPropagation()}
                size="large"
                disabled
              ></sl-input>
            </div>
          </div>

          ${this.user.authData ? html`
            <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
              <div class="field-label">Last Sign In:</div>
              <div class="field-value">
                <sl-input
                  .value=${this.user.authData.lastSignInTime ? this.formatDate(new Date(this.user.authData.lastSignInTime).getTime()) : 'Never'}
                  @click=${(e: Event) => e.stopPropagation()}
                  size="large"
                  disabled
                ></sl-input>
              </div>
            </div>

            <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
              <div class="field-label">Email Verified:</div>
              <div class="field-value">
                <sl-checkbox
                  .checked=${this.user.authData.emailVerified}
                  @click=${(e: Event) => e.stopPropagation()}
                  disabled
                ></sl-checkbox>
              </div>
            </div>

            <div class="field-row" @click=${(e: Event) => e.stopPropagation()}>
              <div class="field-label">Account Status:</div>
              <div class="field-value">
                <sl-input
                  .value=${this.user.authData.disabled ? 'Disabled' : 'Active'}
                  @click=${(e: Event) => e.stopPropagation()}
                  size="large"
                  disabled
                ></sl-input>
              </div>
            </div>
          ` : ''}
        </div>

        <!-- Permissions Section - Only show for non-admin users -->
        ${this.user.model.role !== UserRole.ADMIN ? html`
        <div class="permissions-section">
          <h3>Permissions</h3>
          <div class="permissions-grid">
            ${this.getPermissionResources().map(resource => html`
              <div class="permission-resource">
                <div class="resource-label">${resource.label}</div>
                <div class="permission-actions">
                  <sl-checkbox
                    .checked=${this.hasPermission(resource.key as keyof UserPermissions, 'read')}
                    @sl-change=${(e: Event) => this.handlePermissionChange(
                      resource.key as keyof UserPermissions, 
                      'read', 
                      (e.target as any).checked
                    )}
                    ?disabled=${!this.isEditing}
                  >
                    Read
                  </sl-checkbox>
                  <sl-checkbox
                    .checked=${this.hasPermission(resource.key as keyof UserPermissions, 'write')}
                    @sl-change=${(e: Event) => this.handlePermissionChange(
                      resource.key as keyof UserPermissions, 
                      'write', 
                      (e.target as any).checked
                    )}
                    ?disabled=${!this.isEditing}
                  >
                    Write
                  </sl-checkbox>
                  <sl-checkbox
                    .checked=${this.hasPermission(resource.key as keyof UserPermissions, 'delete')}
                    @sl-change=${(e: Event) => this.handlePermissionChange(
                      resource.key as keyof UserPermissions, 
                      'delete', 
                      (e.target as any).checked
                    )}
                    ?disabled=${!this.isEditing}
                  >
                    Delete
                  </sl-checkbox>
                </div>
              </div>
            `)}
          </div>
        </div>
        ` : ''}

        <div slot="footer" class="dialog-footer">
          ${this.isEditing ? html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleCancel}
              ?disabled=${this.isLoading}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.handleSave}
              ?loading=${this.isLoading}
              ?disabled=${this.isLoading}
            >
              ${this.isLoading ? 'Saving...' : 'Save'}
            </sl-button>
          ` : html`
            <sl-button
              variant="neutral"
              size="large"
              @click=${this.handleEdit}
            >
              Edit
            </sl-button>
            <sl-button
              variant="primary"
              size="large"
              @click=${this.closeModal}
            >
              Done
            </sl-button>
          `}
        </div>
      </sl-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-viewer': UserViewer;
  }
}
